import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import "./Home.css";
import { Link, useNavigate } from "react-router-dom";

import {
  collection,
  onSnapshot,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { sizedUrl, optimizeUrl } from "../utils/uploadImage";
import { optimizeVideoUrl, videoPosterUrl } from "../utils/uploadVideo";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import AdmissionPopup from "../components/AdmissionPopup";

// ─────────────────────────────
// Hero ka text — yahan se edit karo
// ─────────────────────────────
const ADMISSION_YEAR = "";
const HERO_MOTTO = "Where Education Meets Values";
const HERO_FACTS = [
  { value: "3", label: "Campuses" },
  { value: "1500+", label: "Students" },
  { value: "80+", label: "Teachers" },
];

// Default hero image — jab tak Admin Panel se koi image upload nahi hoti
const DEFAULT_HERO_IMG = "/images/Building3.webp";

// Hero slideshow ka gap (milliseconds mein)
const HERO_SLIDE_INTERVAL = 5000;

// ─────────────────────────────
// Animation helpers
// ─────────────────────────────
const EASE = [0.22, 0.8, 0.26, 1];

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.12 },
  transition: { duration: 0.8, ease: EASE },
};

const cardReveal = (i = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: EASE, delay: (i % 3) * 0.09 },
});

const toDate = (ts) => (ts && typeof ts.toDate === "function" ? ts.toDate() : null);

// ─────────────────────────────
// Realtime Firestore list
// ─────────────────────────────
function useCollection(name, direction = "desc") {
  const [data, setData] = useState([]);
  useEffect(() => {
    const q = query(collection(db, name), orderBy("createdAt", direction));
    const unsub = onSnapshot(
      q,
      (snap) => setData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => console.error(`${name} load error:`, error.message)
    );
    return () => unsub();
  }, [name, direction]);
  return data;
}

// ─────────────────────────────
// Top scroll-progress bar
// ─────────────────────────────
function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0;
      el.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return <div ref={ref} className="scroll-progress" aria-hidden="true" />;
}

// ─────────────────────────────
// Section heading
// ─────────────────────────────
const headParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};
const headChild = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};
const lineChild = {
  hidden: { opacity: 0, scaleX: 0 },
  visible: { opacity: 1, scaleX: 1, transition: { duration: 0.8, ease: EASE } },
};

function SectionHeader({ id, eyebrow, title, subtitle, light = false }) {
  return (
    <motion.div
      className={`sec-head ${light ? "is-light" : ""}`}
      variants={headParent}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      {eyebrow && (
        <motion.span className="sec-eyebrow" variants={headChild}>
          {eyebrow}
        </motion.span>
      )}
      <motion.h2 id={id} variants={headChild}>
        {title}
      </motion.h2>
      <motion.span className="sec-line" aria-hidden="true" variants={lineChild} />
      {subtitle && (
        <motion.p className="sec-sub" variants={headChild}>
          {subtitle}
        </motion.p>
      )}
    </motion.div>
  );
}

// ─────────────────────────────
// Count-up number
// ─────────────────────────────
function CountUp({ value, duration = 1600 }) {
  const ref = useRef(null);
  const match = String(value).match(/^(\d+)(.*)$/);
  const end = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : "";
  const [n, setN] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (end === null || !el) return undefined;

    if (!("IntersectionObserver" in window)) {
      setN(end);
      return undefined;
    }

    let raf;
    let started = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          setN(Math.round(end * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        io.disconnect();
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [end, duration]);

  if (end === null) return <span ref={ref}>{value}</span>;
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

// ─────────────────────────────
// Floating stats card
// ─────────────────────────────
function StatsStrip({ facts }) {
  return (
    <section className="stats-strip" aria-label="School at a glance">
      <motion.div
        className="stats-card"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        {facts.map((f, i) => (
          <motion.div
            className="stat"
            key={f.label}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.12 }}
          >
            <strong className="stat-value">
              <CountUp value={f.value} />
            </strong>
            <span className="stat-label">{f.label}</span>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// ─────────────────────────────
// Reusable slider
// ─────────────────────────────
function ImageSlider({
  items,
  renderItem,
  maxPerView = 3,
  autoplay = true,
  variant = "light",
}) {
  return (
    <div className={`slider-wrap ${variant === "dark" ? "is-dark" : ""}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation
        pagination={{ clickable: true, dynamicBullets: items.length > 6 }}
        autoplay={
          autoplay && items.length > 1
            ? { delay: 3500, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false
        }
        rewind
        grabCursor
        centerInsufficientSlides
        speed={650}
        slidesPerView={1.12}
        spaceBetween={14}
        breakpoints={{
          0: { slidesPerView: 1.12, spaceBetween: 14 },
          560: { slidesPerView: 2, spaceBetween: 18 },
          900: { slidesPerView: Math.min(2, maxPerView), spaceBetween: 20 },
          1200: { slidesPerView: Math.min(3, maxPerView), spaceBetween: 24 },
          1600: { slidesPerView: maxPerView, spaceBetween: 26 },
        }}
      >
        {items.map((item, i) => (
          <SwiperSlide key={item.id}>
            <motion.div
              className="slide-anim"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 4) * 0.08 }}
            >
              {renderItem(item)}
            </motion.div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

// ─────────────────────────────
// Image jo load hone par fade-in hoti hai
// ─────────────────────────────
function FadeImg({ className = "", onLoad, onError, alt, ...rest }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    setReady(!!(el && el.complete && el.naturalWidth > 0));
  }, [rest.src]);

  return (
    <img
      ref={ref}
      className={`${className} fade-img ${ready ? "is-loaded" : ""}`}
      alt={alt ?? ""}
      onLoad={(e) => {
        setReady(true);
        if (onLoad) onLoad(e);
      }}
      onError={(e) => {
        setReady(true);
        if (onError) onError(e);
      }}
      {...rest}
    />
  );
}

// ─────────────────────────────
// Photo card: poori image dikhti hai (contain), caption neeche strip mein
// ─────────────────────────────
function PhotoCard({ item, tag, fallbackAlt }) {
  return (
    <figure className="photo-card">
      <div className="photo-frame">
        <FadeImg
          className="photo-main"
          src={item.img}
          alt={item.title || fallbackAlt}
          loading="lazy"
          draggable={false}
        />
        {tag && <span className="photo-tag">{tag}</span>}
      </div>
      {item.title && <figcaption className="photo-caption">{item.title}</figcaption>}
    </figure>
  );
}

// ─────────────────────────────
// Student result card (text card)
// ─────────────────────────────
function ResultCard({ res }) {
  const d = toDate(res.createdAt);
  return (
    <div className="result-card">
      <span className="result-year">{res.year}</span>
      <div className="student-icon">🎓</div>
      <div className="result-info">
        <h3>{res.name}</h3>
        <p>
          <strong>Roll #:</strong> {res.rollNumber}
        </p>
        <p className="result-marks">
          <strong>Marks:</strong> {res.marks}
        </p>
        <span className="result-date">
          📅 {d ? d.toLocaleDateString("en-US") : "No Date"}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────
// Full-width banner slider (image apne natural ratio mein)
// ─────────────────────────────
function BannerSlider({ items }) {
  return (
    <div className="banner-wrap">
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation
        pagination={{ clickable: true }}
        autoplay={
          items.length > 1
            ? { delay: 4200, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false
        }
        rewind
        grabCursor
        speed={750}
        slidesPerView={1}
      >
        {items.map((item, i) => (
          <SwiperSlide key={item.id}>
            <div className="banner-slide">
              <FadeImg
                className="banner-img"
                src={item.img}
                alt={item.title || "Banner"}
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
              />
              {item.title && <div className="banner-caption">{item.title}</div>}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

// ─────────────────────────────
// Fullscreen image viewer with zoom
// ─────────────────────────────
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

function ImageLightbox({ items, index, onClose, onChange }) {
  const [scale, setScale] = useState(1);
  const [ratio, setRatio] = useState(4 / 3);
  const [panning, setPanning] = useState(false);

  const stageRef = useRef(null);
  const prevScale = useRef(1);
  const pan = useRef(null);
  const moved = useRef(false);

  const item = items[index];
  const multiple = items.length > 1;

  const zoomTo = (s) =>
    setScale(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(s * 100) / 100)));
  const resetZoom = () => setScale(1);
  const go = (dir) => onChange((index + dir + items.length) % items.length);

  useEffect(() => {
    setScale(1);
  }, [index]);

  useEffect(() => {
    const el = stageRef.current;
    if (el) {
      if (scale === 1) {
        el.scrollLeft = 0;
        el.scrollTop = 0;
      } else if (prevScale.current === 1) {
        el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
        el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
      }
    }
    prevScale.current = scale;
  }, [scale]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && multiple) go(-1);
      else if (e.key === "ArrowRight" && multiple) go(1);
      else if (e.key === "+" || e.key === "=") zoomTo(scale + 0.5);
      else if (e.key === "-") zoomTo(scale - 0.5);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, scale, items.length]);

  const onMouseDown = (e) => {
    moved.current = false;
    if (scale > 1 && e.button === 0) {
      const el = stageRef.current;
      pan.current = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop };
      setPanning(true);
    }
  };
  const onMouseMove = (e) => {
    if (!pan.current) return;
    const dx = e.clientX - pan.current.x;
    const dy = e.clientY - pan.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved.current = true;
    stageRef.current.scrollLeft = pan.current.l - dx;
    stageRef.current.scrollTop = pan.current.t - dy;
  };
  const endPan = () => {
    pan.current = null;
    setPanning(false);
  };

  if (!item) return null;

  const imgStyle = {
    height: `calc(min(100vh - var(--zv-pady), (100vw - var(--zv-padx)) / ${ratio}) * ${scale})`,
    width: "auto",
    maxWidth: "none",
    maxHeight: "none",
  };

  return createPortal(
    <div className="zv" role="dialog" aria-modal="true" aria-label={item.title || "Result image"}>
      <div className="zv-top">
        <div className="zv-heading">
          <span className="zv-title">{item.title || "Student Result"}</span>
          {multiple && (
            <span className="zv-count">
              {index + 1} / {items.length}
            </span>
          )}
        </div>
        <button type="button" className="zv-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div
        ref={stageRef}
        className={`zv-stage ${scale > 1 ? "can-pan" : ""} ${panning ? "is-dragging" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget && scale === 1 && !moved.current) onClose();
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        onDoubleClick={() => (scale > 1 ? resetZoom() : zoomTo(2))}
      >
        <img
          className="zv-img"
          src={item.full || item.img}
          alt={item.title || "Result"}
          draggable={false}
          style={imgStyle}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setRatio(w / h);
          }}
        />
      </div>

      {multiple && (
        <>
          <button type="button" className="zv-nav zv-prev" onClick={() => go(-1)} aria-label="Previous">
            ‹
          </button>
          <button type="button" className="zv-nav zv-next" onClick={() => go(1)} aria-label="Next">
            ›
          </button>
        </>
      )}

      <div className="zv-toolbar">
        <button type="button" onClick={() => zoomTo(scale - 0.5)} disabled={scale <= MIN_ZOOM} aria-label="Zoom out">
          −
        </button>
        <span className="zv-zoom">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => zoomTo(scale + 0.5)} disabled={scale >= MAX_ZOOM} aria-label="Zoom in">
          +
        </button>
        <button type="button" className="zv-reset" onClick={resetZoom} disabled={scale === 1}>
          Reset
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────
// Video card
// ─────────────────────────────
let activeVideo = null;

const PLAY_ON_HOVER = false;

const inFullscreen = (v) =>
  !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    (v && v.webkitDisplayingFullscreen)
  );

const pauseVideo = (v) => {
  if (v && !v.paused && !inFullscreen(v)) v.pause();
};

const canHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover)").matches;

function VideoCard({ item }) {
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [src, setSrc] = useState(item.src);

  useEffect(() => {
    setSrc(item.src);
    setFailed(false);
  }, [item.src]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 0.35) pauseVideo(videoRef.current);
      },
      { threshold: [0, 0.35, 0.6, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onHide = () => {
      if (document.hidden) pauseVideo(videoRef.current);
    };
    document.addEventListener("visibilitychange", onHide);
    // Cleanup ke liye ref ki current value effect ke andar copy kar li
    const node = videoRef.current;
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      if (activeVideo === node) activeVideo = null;
    };
  }, []);

  const play = (muted = false) => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    setStarted(true);
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };

  return (
    <div
      ref={cardRef}
      className="video-card"
      onMouseEnter={() => {
        if (PLAY_ON_HOVER && canHover()) play(true);
      }}
      onMouseLeave={() => {
        if (canHover()) pauseVideo(videoRef.current);
      }}
    >
      <div className={`video-frame ${started ? "swiper-no-swiping" : ""}`}>
        <video
          ref={videoRef}
          className="vid-el"
          src={src}
          poster={item.poster || undefined}
          preload={item.poster ? "none" : "metadata"}
          playsInline
          controls={started}
          controlsList="nodownload"
          onPlay={(e) => {
            const v = e.currentTarget;
            if (activeVideo && activeVideo !== v) activeVideo.pause();
            activeVideo = v;
          }}
          onPause={(e) => {
            if (activeVideo === e.currentTarget) activeVideo = null;
          }}
          onError={() => {
            if (item.fallback && src !== item.fallback) setSrc(item.fallback);
            else setFailed(true);
          }}
        />

        {!started && !failed && (
          <button
            type="button"
            className="vid-play"
            onClick={() => play(false)}
            aria-label={`Play ${item.title || "video"}`}
          >
            <span className="vid-play-icon" />
          </button>
        )}

        {failed && <div className="vid-error">This video could not be loaded.</div>}
      </div>
      {item.title && <p className="video-title">{item.title}</p>}
    </div>
  );
}

// ═════════════════════════════
// HOME PAGE
// ═════════════════════════════
function Home() {
  const [admissionsOpen, setAdmissionsOpen] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Hero slideshow
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);

  const navigate = useNavigate();

  const news = useCollection("news", "desc");
  const results = useCollection("results", "desc");
  const uploadedCampus = useCollection("campus", "asc");
  const uploadedLife = useCollection("schoolLife", "desc");
  const banners = useCollection("banners", "desc");
  const resultImages = useCollection("resultImages", "desc");
  const videos = useCollection("videos", "desc");

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "settings", "admissions"),
      (snap) => {
        if (snap.exists()) setAdmissionsOpen(snap.data().isOpen !== false);
      },
      (error) => console.error("admissions load error:", error.message)
    );
    return () => unsub();
  }, []);

  // Hero images realtime load (homeHeroes collection, order ke hisaab se)
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "homeHeroes"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setHeroSlides(list.filter((h) => h.imageUrl));
      },
      (error) => console.error("homeHeroes load error:", error.message)
    );
    return () => unsub();
  }, []);

  // Auto-slide: har HERO_SLIDE_INTERVAL ms mein next image
  useEffect(() => {
    if (heroSlides.length < 2) return undefined;
    const t = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroSlides.length);
    }, HERO_SLIDE_INTERVAL);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const heroImgs = useMemo(
    () =>
      heroSlides.length
        ? heroSlides.map((h) => ({ id: h.id, src: sizedUrl(h.imageUrl, "homeHero") }))
        : [{ id: "default", src: DEFAULT_HERO_IMG }],
    [heroSlides]
  );
  const activeHeroIdx = heroImgs.length ? heroIndex % heroImgs.length : 0;
  const currentHero = heroImgs[activeHeroIdx];

  const videoItems = useMemo(
    () =>
      videos
        .filter((v) => v.videoUrl)
        .map((v) => ({
          id: v.id,
          src: optimizeVideoUrl(v.videoUrl, 1280),
          poster: videoPosterUrl(v.videoUrl, 800),
          fallback: v.videoUrl,
          title: v.title,
        })),
    [videos]
  );

  const campusImages = useMemo(
    () =>
      uploadedCampus.map((c) => ({
        id: c.id,
        img: sizedUrl(c.imageUrl, "campus"),
        title: c.title,
      })),
    [uploadedCampus]
  );

  const lifeImages = useMemo(
    () =>
      uploadedLife.map((l) => ({
        id: l.id,
        img: sizedUrl(l.imageUrl, "schoolLife"),
        title: l.title,
      })),
    [uploadedLife]
  );

  const bannerImages = useMemo(
    () =>
      banners.map((b) => ({
        id: b.id,
        img: sizedUrl(b.imageUrl, "banner"),
        title: b.title,
      })),
    [banners]
  );

  const resultImgs = useMemo(
    () =>
      resultImages.map((r) => ({
        id: r.id,
        img: sizedUrl(r.imageUrl, "resultImages"),
        full: optimizeUrl(r.imageUrl, 2000),
        title: r.title,
      })),
    [resultImages]
  );

  const facilities = [
    { icon: "🎓", text: "Modern Classrooms with Smart Boards" },
    { icon: "📚", text: "Well-Stocked Library" },
    { icon: "🏀", text: "Sports Complex and Playground" },
    { icon: "🎭", text: "Arts" },
    { icon: "💻", text: "Computer Labs with Latest Technology" },
    { icon: "🍎", text: "Healthy Cafeteria" },
  ];

  return (
    <div className="home">
      <ScrollProgress />
      <AdmissionPopup />

      {/* ───────── 1. HERO ───────── */}
      <section className="hero-section" role="banner">
        <div className="hero-bg">
          <div className="hero-frame">
            <img
              key={currentHero.id}
              className="hero-bg-img hero-slide-anim"
              src={currentHero.src}
              alt="Academic Group of Schools campus"
              loading="eager"
              decoding="async"
            />

            {heroImgs.length > 1 && (
              <div className="hero-dots">
                {heroImgs.map((h, i) => (
                  <button
                    key={h.id}
                    type="button"
                    className={`hero-dot ${i === activeHeroIdx ? "is-active" : ""}`}
                    onClick={() => setHeroIndex(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Mobile/tablet: image ke upar caption */}
            <div className="hero-caption">
              {admissionsOpen && (
                <span className="hero-badge">🎓 Admissions Open {ADMISSION_YEAR}</span>
              )}
              <div className="hero-caption-body">
                <p className="hero-motto">{HERO_MOTTO}</p>
                <ul className="hero-facts">
                  {HERO_FACTS.map((f) => (
                    <li key={f.label}>
                      <strong>{f.value}</strong> {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Desktop decor */}
          <div className="hero-decor" aria-hidden="true">
            <span className="hero-orb hero-orb-gold" />
            <span className="hero-orb hero-orb-blue" />
          </div>
        </div>

        {/* Text block */}
        <div className="hero-text">
          {admissionsOpen && (
            <span className="hero-pill">
              <i className="pill-dot" aria-hidden="true" />
              Admissions Open {ADMISSION_YEAR}
            </span>
          )}

          <h1>
            Welcome to{" "}
            <span className="hero-grad">Academic Group of Schools &amp; College</span>
          </h1>

          <p>Shaping the future through quality education and values.</p>

          <div className="hero-buttons">
            <a href="#gallery" className="hero-btn">
              🎓 Explore More
            </a>
            {admissionsOpen ? (
              <button type="button" className="apply-btn" onClick={() => navigate("/ApplyForm")}>
                Apply Now
              </button>
            ) : (
              <button type="button" className="admission-btn disabled" disabled>
                Admissions Closed
              </button>
            )}
          </div>
        </div>

        <a className="scroll-cue" href="#campus" aria-label="Scroll down" />
      </section>

      {/* ───────── 2. STATS ───────── */}
      <StatsStrip facts={HERO_FACTS} />

      {/* ───────── 3. BANNER ───────── */}
      {bannerImages.length > 0 && (
        <section className="banner-section">
          <motion.div {...reveal}>
            <BannerSlider items={bannerImages} />
          </motion.div>
        </section>
      )}

      {/* ───────── 4. FACILITIES ───────── */}
      <section className="sec facilities" aria-labelledby="facilities-title">
        <div className="sec-inner">
          <SectionHeader
            id="facilities-title"
            eyebrow="What We Offer"
            title="Our Facilities"
          />
          <div className="facilities-grid">
            {facilities.map((facility, index) => (
              <motion.div key={facility.text} className="reveal-wrap" {...cardReveal(index)}>
                <div className="facility-card">
                  <span className="fac-num" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="fac-icon" aria-hidden="true">
                    {facility.icon}
                  </div>
                  <p>{facility.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── 5. CAMPUS ───────── */}
      <section className="sec campus" id="campus" aria-labelledby="campus-title">
        <div className="sec-inner">
          <SectionHeader
            id="campus-title"
            eyebrow="Our Campuses"
            title="Campus Details"
            subtitle="We have three campuses in this city, each designed to provide an exceptional learning environment. Together, they span over 50 acres and feature modern classrooms, sports facilities, and dedicated spaces for arts and sciences."
          />
          {campusImages.length > 0 ? (
            <motion.div {...reveal}>
              <ImageSlider
                items={campusImages}
                maxPerView={3}
                renderItem={(item) => (
                  <PhotoCard item={item} tag="Campus" fallbackAlt="Campus" />
                )}
              />
            </motion.div>
          ) : (
            <p className="empty-note">Campus images coming soon.</p>
          )}
        </div>
      </section>

      {/* ───────── 6. GALLERY ───────── */}
      <section className="sec gallery" id="gallery" aria-labelledby="gallery-title">
        <div className="sec-inner">
          <SectionHeader id="gallery-title" eyebrow="Gallery" title="Our School Life" />
          {lifeImages.length > 0 ? (
            <motion.div {...reveal}>
              <ImageSlider
                items={lifeImages}
                maxPerView={4}
                renderItem={(item) => <PhotoCard item={item} fallbackAlt="School life" />}
              />
            </motion.div>
          ) : (
            <p className="empty-note">School life photos coming soon.</p>
          )}
        </div>
      </section>

      {/* ───────── 7. VIDEOS ───────── */}
      {videoItems.length > 0 && (
        <section className="sec videos-section" id="videos" aria-labelledby="videos-title">
          <div className="sec-inner">
            <SectionHeader
              id="videos-title"
              eyebrow="Watch"
              title="School Videos"
              subtitle="Glimpses of school life, events and activities. Swipe or use the arrows to watch more."
              light
            />
            <motion.div {...reveal}>
              <ImageSlider
                items={videoItems}
                maxPerView={3}
                autoplay={false}
                variant="dark"
                renderItem={(item) => <VideoCard item={item} />}
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* ───────── 8. RESULTS ───────── */}
      <section className="sec results-section" aria-labelledby="results-title">
        <div className="sec-inner">
          <SectionHeader
            id="results-title"
            eyebrow="Achievements"
            title="Student Results"
            subtitle="Proud moments of our hardworking students. Swipe or use the arrows to see more."
          />

          {resultImgs.length > 0 && (
            <motion.div className="results-slider" {...reveal}>
              <ImageSlider
                items={resultImgs}
                maxPerView={3}
                renderItem={(item) => (
                  <button
                    type="button"
                    className="result-image-card"
                    onClick={() =>
                      setLightboxIndex(resultImgs.findIndex((r) => r.id === item.id))
                    }
                    aria-label={`View ${item.title || "result"} in full size`}
                  >
                    <div className="result-image-frame">
                      <FadeImg
                        className="fit-img"
                        src={item.img}
                        alt={item.title || "Result"}
                        loading="lazy"
                        draggable={false}
                      />
                      <span className="result-zoom-badge">🔍 Click to zoom</span>
                    </div>
                    {item.title && <p>{item.title}</p>}
                  </button>
                )}
              />
            </motion.div>
          )}

          {results.length > 0 ? (
            <motion.div className="results-cards-slider" {...reveal}>
              <ImageSlider
                items={results}
                maxPerView={4}
                renderItem={(res) => <ResultCard res={res} />}
              />
            </motion.div>
          ) : (
            <p className="empty-note">No results announced yet.</p>
          )}
        </div>
      </section>

      {/* ───────── 9. NEWS ───────── */}
      <section className="sec news-section" aria-labelledby="news-title">
        <div className="sec-inner">
          <SectionHeader id="news-title" eyebrow="Updates" title="Latest Events & News" />
          <div className="news-grid">
            {news.length > 0 ? (
              news.map((item, i) => {
                const d = toDate(item.createdAt);
                return (
                  <motion.div key={item.id} className="reveal-wrap" {...cardReveal(i)}>
                    <article className="news-card">
                      <div className="news-badge">
                        {d ? (
                          <>
                            <b>{d.getDate()}</b>
                            <span>{d.toLocaleDateString("en-US", { month: "short" })}</span>
                            <small>{d.getFullYear()}</small>
                          </>
                        ) : (
                          <b>—</b>
                        )}
                      </div>
                      <div className="news-body">
                        <h3>{item.title}</h3>
                        <span className="news-date">
                          📅{" "}
                          {d
                            ? d.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "No Date"}
                        </span>
                      </div>
                    </article>
                  </motion.div>
                );
              })
            ) : (
              <p className="empty-note">No events yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* ───────── 10. CTA ───────── */}
      <section className="cta" aria-labelledby="cta-title">
        <div className="cta-inner">
          <motion.h2
            id="cta-title"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            Ready to Join Us?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          >
            Contact us today for admission inquiries or to book a campus tour.
          </motion.p>
          <motion.div
            className="cta-actions"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
          >
            <Link to="/contact" className="contact-btn">
              📞 Contact Us
            </Link>
            {admissionsOpen && (
              <button type="button" className="cta-apply" onClick={() => navigate("/ApplyForm")}>
                Apply Now →
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {lightboxIndex !== null && resultImgs[lightboxIndex] && (
        <ImageLightbox
          items={resultImgs}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}
    </div>
  );
}

export default Home;